import Vue from 'vue';
import Api from './Api.js';
import EventBus from './event-bus.js';

// Globalny menedżer opóźnionej wysyłki ("cofnij wysyłkę").
// Po kliknięciu "Wyślij" wiadomość trafia tutaj i jest faktycznie wysyłana dopiero
// po upływie okna anulowania. Menedżer żyje poza komponentem ticketu, więc wysyłka
// dokończy się nawet po opuszczeniu widoku sprawy. Treść do przywrócenia (przy anulowaniu
// lub błędzie) trzymana jest w buforze i odtwarzana po powrocie do sprawy.

const DEFAULT_DELAY_MS = parseInt(process.env.VUE_APP_UNDO_SEND_MS, 10) > 0
    ? parseInt(process.env.VUE_APP_UNDO_SEND_MS, 10)
    : 5000;
const TICK_MS = 100;

const state = Vue.observable({ items: [] });
const internal = {};       // id -> { interval, payload, ticketId, restore }
const restoreBuffer = {};  // ticketId -> restore (treść do odtworzenia)
let seq = 0;

function findIndex(id) {
    return state.items.findIndex(i => i.id === id);
}

function remove(id) {
    const ctx = internal[id];
    if (ctx && ctx.interval) clearInterval(ctx.interval);
    delete internal[id];
    const idx = findIndex(id);
    if (idx !== -1) state.items.splice(idx, 1);
}

async function fire(id) {
    const ctx = internal[id];
    const idx = findIndex(id);
    if (!ctx || idx === -1) return;
    if (state.items[idx].status !== 'pending') return;
    if (ctx.interval) clearInterval(ctx.interval);
    const ticketNumber = state.items[idx].ticketNumber;
    state.items[idx].status = 'sending';
    try {
        const result = await Api.imap.tickets.reply(ctx.ticketId, ctx.payload);
        EventBus.$emit('pending-send:sent', { id, ticketId: ctx.ticketId, ticketNumber, result });
        remove(id);
    } catch (e) {
        const msg = (e && e.data && e.data.error)
            || (e && e.response && e.response.data && e.response.data.error)
            || (e && e.message) || 'Nieznany błąd';
        if (ctx.restore) restoreBuffer[ctx.ticketId] = ctx.restore;
        EventBus.$emit('pending-send:error', { id, ticketId: ctx.ticketId, ticketNumber, message: msg, restore: ctx.restore || null });
        remove(id);
    }
}

function schedule(opts) {
    const id = 'ps_' + (++seq) + '_' + Date.now();
    const delayMs = opts.delayMs || DEFAULT_DELAY_MS;
    const item = {
        id,
        ticketId: String(opts.ticketId),
        ticketNumber: opts.ticketNumber || '',
        delayMs,
        deadline: Date.now() + delayMs,
        remainingMs: delayMs,
        status: 'pending'
    };
    internal[id] = {
        payload: opts.payload,
        ticketId: String(opts.ticketId),
        restore: opts.restore || null,
        interval: null
    };
    state.items.push(item);

    internal[id].interval = setInterval(() => {
        const idx = findIndex(id);
        if (idx === -1) return;
        const remaining = state.items[idx].deadline - Date.now();
        if (remaining <= 0) {
            state.items[idx].remainingMs = 0;
            fire(id);
        } else {
            state.items[idx].remainingMs = remaining;
        }
    }, TICK_MS);

    return id;
}

function cancel(id) {
    const ctx = internal[id];
    const idx = findIndex(id);
    if (!ctx || idx === -1) return;
    if (state.items[idx].status !== 'pending') return; // za późno – już wysłane/wysyłane
    if (ctx.interval) clearInterval(ctx.interval);
    if (ctx.restore) restoreBuffer[ctx.ticketId] = ctx.restore;
    EventBus.$emit('pending-send:cancelled', { id, ticketId: ctx.ticketId, restore: ctx.restore || null });
    remove(id);
}

// Pobiera (i czyści) treść do odtworzenia dla danej sprawy.
function consumeRestore(ticketId) {
    const key = String(ticketId);
    const r = restoreBuffer[key];
    if (r) delete restoreBuffer[key];
    return r || null;
}

function hasPendingForTicket(ticketId) {
    const key = String(ticketId);
    return state.items.some(i => i.ticketId === key && i.status === 'pending');
}

export default {
    state,
    schedule,
    cancel,
    remove,
    consumeRestore,
    hasPendingForTicket,
    DEFAULT_DELAY_MS
};
